import { useState, useCallback } from 'react';
import { EntityType } from '../../../../../generated/data-contracts';
import { useRolePermissionsApi } from './useRolePermissionsApi';

export const useEntityDataLoader = (existingPermissions: any[]) => {
  const { api, notify, loadAllPages, filterExistingPermissions } =
    useRolePermissionsApi();

  // Estados para listas de entidades
  const [clients, setClients] = useState<any[]>([]);
  const [plants, setPlants] = useState<any[]>([]);
  const [dashboardGroups, setDashboardGroups] = useState<any[]>([]);
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [diagrams, setDiagrams] = useState<any[]>([]);

  // Loading states
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingPlants, setLoadingPlants] = useState(false);
  const [loadingDashboardGroups, setLoadingDashboardGroups] = useState(false);
  const [loadingDashboards, setLoadingDashboards] = useState(false);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [loadingDiagrams, setLoadingDiagrams] = useState(false);

  // Cargar clientes
  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const clientsData = await loadAllPages(
        'clients',
        {},
        { field: 'clientName', order: 'ASC' }
      );
      // Filtrar clientes que ya tienen permisos
      const filteredClients = filterExistingPermissions(
        clientsData,
        EntityType.CLIENT,
        existingPermissions
      );
      setClients(filteredClients);
    } catch (error) {
      notify('Error al cargar clientes', { type: 'error' });
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, [loadAllPages, notify, filterExistingPermissions, existingPermissions]);

  // Cargar plantas
  const loadPlants = useCallback(async () => {
    setLoadingPlants(true);
    try {
      const plantsData = await loadAllPages(
        'plants',
        {},
        { field: 'plantName', order: 'ASC' }
      );
      // Filtrar plantas que ya tienen permisos
      const filteredPlants = filterExistingPermissions(
        plantsData,
        EntityType.PLANT,
        existingPermissions
      );
      setPlants(filteredPlants);
    } catch (error) {
      notify('Error al cargar plantas', { type: 'error' });
      setPlants([]);
    } finally {
      setLoadingPlants(false);
    }
  }, [loadAllPages, notify, filterExistingPermissions, existingPermissions]);

  // Cargar dashboard groups por planta usando endpoint específico
  const loadDashboardGroups = useCallback(
    async (plantId: number) => {
      setLoadingDashboardGroups(true);
      try {
        const response = await api.dashboardGroupsByPlantList({ id: plantId });
        console.log('Dashboard groups response:', response);
        // Como el tipo dice void, vamos a inspeccionar la respuesta real
        const groupsData =
          (response as any)?.data?.items || (response as any)?.data || [];
        // Filtrar grupos que ya tienen permisos
        const filteredGroups = filterExistingPermissions(
          Array.isArray(groupsData) ? groupsData : [],
          EntityType.DASHBOARDGROUP,
          existingPermissions
        );
        setDashboardGroups(filteredGroups);
      } catch (error) {
        console.error('Error loading dashboard groups:', error);
        notify('Error al cargar grupos de dashboard', { type: 'error' });
        setDashboardGroups([]);
      } finally {
        setLoadingDashboardGroups(false);
      }
    },
    [api, notify, filterExistingPermissions, existingPermissions]
  );

  // Cargar dashboard groups por planta SIN FILTRAR (para selector de dashboards)
  const loadDashboardGroupsUnfiltered = useCallback(
    async (plantId: number) => {
      setLoadingDashboardGroups(true);
      try {
        const response = await api.dashboardGroupsByPlantList({ id: plantId });
        console.log('Dashboard groups response (unfiltered):', response);
        // Como el tipo dice void, vamos a inspeccionar la respuesta real
        const groupsData =
          (response as any)?.data?.items || (response as any)?.data || [];
        // NO filtrar grupos - mostrar todos para poder seleccionar dashboards
        setDashboardGroups(Array.isArray(groupsData) ? groupsData : []);
      } catch (error) {
        console.error('Error loading dashboard groups (unfiltered):', error);
        notify('Error al cargar grupos de dashboard', { type: 'error' });
        setDashboardGroups([]);
      } finally {
        setLoadingDashboardGroups(false);
      }
    },
    [api, notify]
  );

  // Cargar dashboards por grupo usando endpoint específico
  const loadDashboards = useCallback(
    async (dashboardGroupId: number) => {
      setLoadingDashboards(true);
      try {
        const response =
          await api.dashboardsByDashboardGroupDetail(dashboardGroupId);
        console.log('Dashboards response:', response);
        // Como el tipo dice void, vamos a inspeccionar la respuesta real
        const dashboardsData =
          (response as any)?.data?.items || (response as any)?.data || [];
        // Filtrar dashboards que ya tienen permisos
        const filteredDashboards = filterExistingPermissions(
          Array.isArray(dashboardsData) ? dashboardsData : [],
          EntityType.DASHBOARD,
          existingPermissions
        );
        setDashboards(filteredDashboards);
      } catch (error) {
        console.error('Error loading dashboards:', error);
        notify('Error al cargar dashboards', { type: 'error' });
        setDashboards([]);
      } finally {
        setLoadingDashboards(false);
      }
    },
    [api, notify, filterExistingPermissions, existingPermissions]
  );

  // Cargar alertas por zona usando endpoint específico
  const loadAlerts = useCallback(
    async (zoneId: number) => {
      setLoadingAlerts(true);
      try {
        const response = await api.alertsByZoneDetail(zoneId);
        console.log('Alerts response:', response);
        // Como el tipo dice void, vamos a inspeccionar la respuesta real
        const alertsData =
          (response as any)?.data?.items || (response as any)?.data || [];
        // Filtrar alertas que ya tienen permisos
        const filteredAlerts = filterExistingPermissions(
          Array.isArray(alertsData) ? alertsData : [],
          EntityType.ALERT,
          existingPermissions
        );
        setAlerts(filteredAlerts);
      } catch (error) {
        console.error('Error loading alerts:', error);
        notify('Error al cargar alertas', { type: 'error' });
        setAlerts([]);
      } finally {
        setLoadingAlerts(false);
      }
    },
    [api, notify, filterExistingPermissions, existingPermissions]
  );

  // Cargar diagramas por planta usando endpoint específico
  const loadDiagrams = useCallback(
    async (plantId: number) => {
      setLoadingDiagrams(true);
      try {
        const response = await api.diagramsByPlantDetail(plantId);
        console.log('Diagrams response:', response);
        // Como el tipo dice void, vamos a inspeccionar la respuesta real
        const diagramsData =
          (response as any)?.data?.items || (response as any)?.data || [];
        // Filtrar diagramas que ya tienen permisos
        const filteredDiagrams = filterExistingPermissions(
          Array.isArray(diagramsData) ? diagramsData : [],
          EntityType.DIAGRAM,
          existingPermissions
        );
        setDiagrams(filteredDiagrams);
      } catch (error) {
        console.error('Error loading diagrams:', error);
        notify('Error al cargar diagramas', { type: 'error' });
        setDiagrams([]);
      } finally {
        setLoadingDiagrams(false);
      }
    },
    [api, notify, filterExistingPermissions, existingPermissions]
  );

  const resetData = useCallback(() => {
    setClients([]);
    setPlants([]);
    setDashboardGroups([]);
    setDashboards([]);
    setAlerts([]);
    setDiagrams([]);
  }, []);

  return {
    // Estados
    clients,
    plants,
    dashboardGroups,
    dashboards,
    alerts,
    diagrams,
    // Loading states
    loadingClients,
    loadingPlants,
    loadingDashboardGroups,
    loadingDashboards,
    loadingAlerts,
    loadingDiagrams,
    // Funciones
    loadClients,
    loadPlants,
    loadDashboardGroups,
    loadDashboardGroupsUnfiltered,
    loadDashboards,
    loadAlerts,
    loadDiagrams,
    resetData,
  };
};
